import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex, Text, Section, IconButton, ScrollArea } from "@radix-ui/themes";
import { Cross2Icon, TrashIcon } from "@radix-ui/react-icons";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { Files02Icon } from "@/v2/assets/icons/Files02Icon";
import { useOpportunityGroups } from "@/api/useOpportunityGroups";
import { Search } from "@/components/shared/inputs/search";
import { DateRangePicker } from "@/components/shared/date-range-picker/DateRangePicker";
import { PageEmptyState } from "@/components/page-empty-state/PageEmptyState";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useOnboarding } from "@/api/onboarding";
import { Link } from "@/components/shared/link/Link";
import { useKnowledgeTracking } from "@/features/knowledge/useKnowledgeTracking";
import { useArticles, useArticlesUsage } from "@/api/useArticles";
import styles from "./styles.module.scss";
import {
  ArticleRow,
  ArticleTicketsTrend,
} from "./articles-table.types";
import { useArticlesSearchParams } from "./useArticlessSearchParams";
import { DeleteArticleDialog } from "./DeleteArticleDialog";
import { Badge, Skeleton } from "@radix-ui/themes";
import {
  getArticleSource,
  articleSourceLabel,
  articleSourceColor,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ArticleDetail } from "./ArticleDetail";

// utils for date formatting
const getDayWithOrdinal = (day: number) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const formatUpdatedAt = (dateString?: string | Date) => {
  if (!dateString) return null;
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  // e.g. "3d March 2025, 12:00"
  const day = getDayWithOrdinal(date.getDate());
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hour}:${minute}`;
};


export const KnowledgeArticles = () => {
  const { hasKnowledgeBaseIntegration } = useOnboarding();
  const accessLabelsEnabled = useFlag("articleAccessLabels");
  const {
    trackArticleSearched,
    trackFilterApplied,
    trackDateRangeChanged,
    trackArticleViewed,
  } = useKnowledgeTracking();

  const { searchPhrase, setSearchPhrase, dateRange, setDateRange } =
    useArticlesSearchParams();

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [knowledgeGapsExists, setKnowledgeGapsExists] =
    useState<boolean>(false);
  const isFirstRender = useRef(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<ArticleRow | null>(null);

  const {
    data: articlesData,
    isLoading,
    refetch: refetchArticles,
  } = useArticles({
    findBy: searchPhrase,
    startDate: dateRange.dateFrom,
    page: page,
    endDate: dateRange.dateTo,
    enabled: true,
  });

  const articleIds = useMemo(
    () => articles.map((article) => article.articleId),
    [articles]
  );

  const { data: articlesUsageData } = useArticlesUsage({
    articleIds,
    startDate: dateRange.dateFrom,
    endDate: dateRange.dateTo,
    enabled: articleIds.length > 0,
  });
  const articlesUsage = articlesUsageData?.ticketsCount as {
    [key: string]: { relatedTickets: number; ticketTrend: number };
  };

  useEffect(() => {
    setPage(1);
    setArticles([]);
    if (!isFirstRender.current) {
      if (searchPhrase) {
        trackArticleSearched(searchPhrase, articles.length);
      }
      trackDateRangeChanged({
        from: dateRange.dateFrom || "",
        to: dateRange.dateTo || "",
      });
    } else {
      isFirstRender.current = false;
    }
  }, [searchPhrase, dateRange]);

  useEffect(() => {
    setHasNextPage(articlesData?.length === 40);
    if (articlesData && Array.isArray(articlesData)) {
      if (page === 1) {
        // Replace on first page
        setArticles(articlesData as ArticleRow[]);
      } else {
        // Append on subsequent pages, but deduplicate by articleId
        setArticles((prev) => {
          const existingIds = new Set(prev.map((article) => article.articleId));
          const newArticles = (articlesData as ArticleRow[]).filter(
            (article) => !existingIds.has(article.articleId)
          );
          return [...prev, ...newArticles];
        });
      }
    }
  }, [articlesData, page]);

  useEffect(() => {
    if (articlesUsage) {
      setArticles((prevArticles) =>
        prevArticles.map((article) => {
          const relatedTickets = articlesUsage[article.articleId];
          return {
            ...article,
            relatedTickets: relatedTickets?.relatedTickets || 0,
            ticketTrend: relatedTickets?.ticketTrend || 0,
          };
        })
      );
    }
  }, [articlesUsage]);

  const {
    data: opportunityGroupsExists,
    isLoading: isLoadingOpportunityGroupsExists,
  } = useOpportunityGroups({
    enabled: !knowledgeGapsExists,
    type: ["knowledge"],
  });

  useEffect(() => {
    if (
      opportunityGroupsExists &&
      Array.isArray(opportunityGroupsExists) &&
      opportunityGroupsExists.length > 0
    ) {
      setKnowledgeGapsExists(true);
    }
  }, [opportunityGroupsExists]);

  const isEmpty = useMemo(
    () =>
      !isLoadingOpportunityGroupsExists &&
      !opportunityGroupsExists &&
      page === 1 &&
      articlesData?.length === 0,
    [articlesData, page, opportunityGroupsExists]
  );

  const handleDeleteClick = useCallback(
    (articleId: string, articleTitle: string) => {
      setArticleToDelete({ id: articleId, title: articleTitle });
      setDeleteDialogOpen(true);
    },
    []
  );

  const handleDeleteSuccess = useCallback(() => {
    setPage(1);
    setArticles([]);
    refetchArticles();
  }, [refetchArticles]);

  const tableColumns = useMemo<ColumnDef<ArticleRow>[]>(() => {
    const baseColumns: ColumnDef<ArticleRow>[] = [
      {
        id: "title",
        header: "Articles",
        accessorKey: "title",
        cell: ({ row }) => {
          return (
            <div
              dir="auto"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Text size="2" weight="regular">
                {row.original.title}
              </Text>
            </div>
          );
        },
        meta: {
          className: "w-[320px]",
          cellTooltip: true,
        },
      },
    ];

    if (!selectedArticleId) {
      baseColumns.push(
        {
          id: "source",
          header: "Source",
          accessorKey: "external",
          cell: ({ row }) => {
            const article = row.original;
            const source = getArticleSource(article.url);
            return (
              <Flex gap="1" align="center">
                <Badge size="1" color={articleSourceColor[source] as any} variant="soft">
                  {articleSourceLabel[source]}
                </Badge>
                {!article.external && (
                  <Badge size="1" color="red" variant="soft">
                    Internal
                  </Badge>
                )}
              </Flex>
            );
          },
          meta: {
            cellTooltip: false,
            cellTooltipContent: (row: ArticleRow) => {
              const source = getArticleSource(row.url);
              if (source === "unknown") {
                try {
                  return new URL(row.url).hostname;
                } catch {}
              }
              return undefined;
            },
          },
        },
        {
          id: "relatedTickets",
          header: "Related Tickets",
          accessorKey: "relatedTickets",
          cell: ({ row }) => {
            const article = row.original;
            return article.relatedTickets === undefined ? (
              <Skeleton>related tickets</Skeleton>
            ) : (
              <Text size="2">
                {article.relatedTickets} in selected period
              </Text>
            );
          },
        },
        {
          id: "ticketTrend",
          header: "Ticket Trend",
          accessorKey: "ticketTrend",
          cell: ({ row }) => {
            const article = row.original;
            return article.relatedTickets === undefined ? (
              <Skeleton>ticket trend</Skeleton>
            ) : (
              <ArticleTicketsTrend ticketTrend={article.ticketTrend} />
            );
          },
        },
        {
          id: "updatedAt",
          header: "Updated At",
          accessorKey: "updatedAt",
          cell: ({ row }) => {
            return <Text>{row.original.updatedAt}</Text>;
          },
        },
        ...(accessLabelsEnabled
          ? [
              {
                id: "labels",
                header: "Labels",
                accessorKey: "accessLabels",
                cell: ({ row }: { row: { original: ArticleRow } }) => {
                  const labels = row.original.accessLabels || [];
                  if (labels.length === 0) {
                    return <Text size="2" color="gray">-</Text>;
                  }
                  return (
                    <Flex gap="1" wrap="wrap">
                      {labels.slice(0, 2).map((label: string, index: number) => (
                        <Badge key={index} size="1" variant="soft">
                          {label}
                        </Badge>
                      ))}
                      {labels.length > 2 && (
                        <Badge size="1" variant="soft" color="gray">
                          +{labels.length - 2}
                        </Badge>
                      )}
                    </Flex>
                  );
                },
              },
            ]
          : []),
        {
          id: "actions",
          header: "",
          cell: ({ row }) => {
            return (
              <Flex justify="end" p="2">
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.original.articleId, row.original.title);
                  }}
                >
                  <TrashIcon />
                </IconButton>
              </Flex>
            );
          },
          meta: {
            className: "w-[80px]",
          },
        }
      );
    }

    return baseColumns;
  }, [handleDeleteClick, selectedArticleId, accessLabelsEnabled]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "title", desc: false },
  ]);

  const handleRowClick = useCallback(
    (row: ArticleRow) => {
      trackArticleViewed(row.articleId, row.title);
      if (selectedArticleId === row.articleId) {
        setSelectedArticleId(null);
        setSelectedArticle(null);
      } else {
        setSelectedArticleId(row.articleId);
        setSelectedArticle(row);
      }
    },
    [trackArticleViewed, selectedArticleId]
  );

  if (isEmpty) {
    return (
      <PageEmptyState
        title={"No articles synced with Quack yet"}
        description={
          "Articles from your knowledge base will appear here once they're synced with Quack. Connect your knowledge sources to get started."
        }
        svg={"knowledge-view"}
      >
        {!hasKnowledgeBaseIntegration && (
          <div>
            <Link to={"/getting-started"}>Connect knowledge sources</Link>
          </div>
        )}
      </PageEmptyState>
    );
  }

  return (
    <PageLayoutV2>
      <Flex height="100%" position="relative">
        <Flex
          flexShrink="1"
          width={selectedArticleId ? "40%" : "100%"}
          minWidth="400px"
          maxWidth={selectedArticleId ? "600px" : "100%"}
          direction="column"
          style={selectedArticleId ? { borderRight: "1px solid var(--gray-6)" } : {}}
        >
          <PageHeader
            title="Articles"
            description="Articles synced with Quack from your knowledge base"
            icon={
              <Files02Icon
                size={24}
                fill={PageColors[ENavigate.KNOWLEDGE_VIEW] || "gray"}
              />
            }
          />
          <Section size="2" p="6">
            <Flex direction={"column"} gap={"8"}>
              <Flex
                style={{
                  padding: "0 4px",
                  width: "100%",
                }}
                justify={"between"}
                align={"center"}
              >
                <Flex gap={"3"} align={"center"}>
                  <Text weight={"medium"} size={"3"}>
                    Articles
                  </Text>
                  <Text size={"2"} weight={"bold"} color="gray">
                    {articles && articles?.length}
                  </Text>
                  {!selectedArticleId && (
                    <Search
                      setSearchPhrase={setSearchPhrase}
                      placeholder="Find"
                      debounce={500}
                      context="knowledge_articles"
                      initialValue={searchPhrase}
                    />
                  )}
                </Flex>
                {!selectedArticleId && (
                  <Flex gap={"1"} align={"center"}>
                    <DateRangePicker value={dateRange} onChange={setDateRange} />
                  </Flex>
                )}
              </Flex>
            </Flex>
            {articles.length === 0 && !isLoading ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                p="8"
                gap="2"
              >
                <Text size="3" color="gray" weight="medium">
                  No articles found
                </Text>
                <Text size="2" color="gray">
                  Try adjusting your search or date range filters
                </Text>
              </Flex>
            ) : (
              <ScrollArea style={{ maxHeight: "calc(100vh - 300px)" }}>
                <SimpleTable
                  data={articles.map((article) => ({
                    ...article,
                    updatedAt: formatUpdatedAt(article.updatedAt) ?? "-",
                  }))}
                  columns={tableColumns}
                  loading={isLoading && page === 1}
                  onRowClicked={handleRowClick}
                  defaultSorting={sorting}
                  selectedRowId={selectedArticleId || undefined}
                  getRowId={(row) => row.articleId}
                />
              </ScrollArea>
            )}
            {hasNextPage && (
              <Flex justify="center" p="4">
                <Text
                  size="2"
                  color="gray"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (!isLoading) {
                      setPage(page + 1);
                    }
                  }}
                >
                  {isLoading ? "Loading..." : "Load more"}
                </Text>
              </Flex>
            )}
          </Section>
        </Flex>

        {selectedArticleId && selectedArticle && (
          <Flex
            flexGrow="1"
            width="100%"
            direction="column"
            style={{ position: "relative" }}
          >
            <Flex
              p="4"
              align="center"
              justify="between"
              style={{ borderBottom: "1px solid var(--gray-6)" }}
            >
              <Text size="2" weight="medium" style={{ fontWeight: 500 }}>
                {selectedArticle.title}
              </Text>
              <IconButton
                variant="ghost"
                size="1"
                onClick={() => {
                  setSelectedArticleId(null);
                  setSelectedArticle(null);
                }}
                aria-label="Close"
              >
                <Cross2Icon width={14} height={14} />
              </IconButton>
            </Flex>
            <Flex flexGrow="1" style={{ overflow: "auto" }}>
              <ArticleDetail
                article={selectedArticle}
                onClose={() => {
                  setSelectedArticleId(null);
                  setSelectedArticle(null);
                }}
                onRefreshArticles={async () => {
                  setPage(1);
                  setArticles([]);
                  await refetchArticles();
                }}
              />
            </Flex>
          </Flex>
        )}
      </Flex>

      {articleToDelete && (
        <DeleteArticleDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          articleId={articleToDelete.id}
          articleTitle={articleToDelete.title}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </PageLayoutV2>
  );
};
